import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { getLoginRoutePath } from '../../app.routes';
import { AlertService } from '../alert/alert.service';
import { AuthService } from '../auth/auth.service';
import { LoggingService } from '../logging/logging.service';
import { UuidService } from '../uuid/uuid.service';
import { UserContext, createUserContext } from './user-context';
import { UserContextService } from './user-context.service';

/**
 * Resolver that loads user context for protected routes
 * Handles authentication, user data loading, and impersonation logic
 * Careful - this is not reactive as much as it looks like it should be.
 */
export const userContextResolver: ResolveFn<UserContext> = async (route) => {
  const alertService = inject(AlertService);
  const authService = inject(AuthService);
  const loggingService = inject(LoggingService);
  const router = inject(Router);
  const userContextService = inject(UserContextService);
  const uuidService = inject(UuidService);

  try {
    // Get the user ID from route parameters
    const userId = route.paramMap.get('userId');
    if (!userId) {
      throw new Error('User ID is required in route parameters');
    }

    // Validate that userId is a valid UUID format
    if (!uuidService.isValidUUID(userId)) {
      await alertService.createErrorNotice('Invalid user ID format');
      router.navigateByUrl(getLoginRoutePath());
      throw new Error('Invalid user ID format');
    }

    // Verify user is authenticated
    const authUser = await authService.getAuthUser();
    if (!authUser) {
      router.navigateByUrl(getLoginRoutePath());
      throw new Error('User not authenticated');
    }

    // Get session user and all users
    const sessionUser = await authService.getSessionUser();
    if (!sessionUser) {
      router.navigateByUrl(getLoginRoutePath());
      throw new Error('Session user not found');
    }

    // Initialize user context service if not already initialized
    // This ensures we have fresh user data
    await userContextService.initialize();

    // Get all users from the reactive stream
    const allUsers = await firstValueFrom(userContextService.users$);

    // Find the session user in the users list
    const sessionUserFromDb = allUsers.find(
      (user) => user.email === authUser.email,
    );
    if (!sessionUserFromDb) {
      await alertService.createErrorNotice('User profile not found');
      router.navigateByUrl(getLoginRoutePath());
      throw new Error('Session user not found in database');
    }

    // Check if the requested user exists and can be accessed
    const targetUser = allUsers.find((user) => user.id === userId);
    if (!targetUser) {
      // User exists but current user doesn't have access - redirect to access request page
      // Use session user ID in route and target user ID as param, with current URL as redirect
      const currentUrl = encodeURIComponent(
        window.location.pathname + window.location.search,
      );
      const accessUrl = `/user/${sessionUserFromDb.id}/access/${userId}?redirect=${currentUrl}`;
      router.navigateByUrl(accessUrl);

      // Return a fallback user context for the session user instead of throwing
      return createUserContext({
        userId: sessionUserFromDb.id,
        sessionUserId: sessionUserFromDb.id,
        allUsers,
      });
    }

    // Note: Permission checking is handled at the database level via RLS policies
    // If targetUser exists in allUsers, the session user has permission to view them

    // Create and return the user context
    const userContext = createUserContext({
      userId,
      sessionUserId: sessionUserFromDb.id,
      allUsers,
    });

    return userContext;
  } catch (error) {
    // Log error for debugging
    loggingService.error('Error in userContextResolver:', error);

    // Re-throw to let Angular handle the error
    throw error;
  }
};
